import type { ContourTexture } from '~/engine/data/structure/ContourTexture';

/**
 * The single create -> write -> record tail shared by every structure
 * operation (symmetric/asymmetric margin, ring, boolean). Callers compute the
 * geometry upstream (direct-to-engine SDF, byte-identical per caller) and hand
 * the finished handle here; this seam never recomputes.
 *
 * The recordContourEdit step is the unification's reason to exist: it is the
 * one place every geometry write becomes an audited change. Order is load
 * bearing — the edit is recorded only after the write lands.
 */

/** Computed geometry ready to write; never recomputed by the seam. */
export interface GeometryWrite {
    readonly texture: ContourTexture;
    /** Engine undo-history + replaceTexture label, e.g. 'Symmetric Margin'. */
    readonly label: string;
}

/**
 * The structural slice of an engine destination ROI the seam touches. The real
 * RegionOfInterest satisfies it; tests pass a fake.
 */
export interface DestinationRoi {
    readonly roiNumber: number;
    readonly volumeCm3: number;
    replaceTexture(texture: ContourTexture, label: string): void;
}

export interface ApplyComputedGeometryDeps {
    /** Create a NEW destination ROI by name (never overwrite); null on failure. */
    createDestinationRoi(newRoiName: string): Promise<DestinationRoi | null>;
    /** Engine refresh after the write (env.triggerContourLayerUpdate). */
    triggerContourLayerUpdate(): void;
    /** Record the write as a tracked, audited contour edit on the new ROI. */
    recordContourEdit(dest: DestinationRoi): void;
}

export interface AppliedGeometry {
    readonly roiNumber: number;
    readonly volumeCm3: number;
}

export async function applyComputedGeometry(
    newRoiName: string,
    geometry: GeometryWrite,
    deps: ApplyComputedGeometryDeps
): Promise<AppliedGeometry> {
    // The seam owns the computed texture until the ROI adopts it
    // (replaceTexture): free it on a failure BEFORE adoption (else it leaks),
    // never after (the ROI now owns a live, rendered texture).
    let dest: DestinationRoi | null;
    try {
        dest = await deps.createDestinationRoi(newRoiName);
    } catch (err) {
        geometry.texture.delete();
        throw err;
    }
    if (!dest) {
        geometry.texture.delete();
        throw new Error('failed to create the new structure');
    }
    dest.replaceTexture(geometry.texture, geometry.label);
    deps.triggerContourLayerUpdate();
    deps.recordContourEdit(dest);
    return { roiNumber: dest.roiNumber, volumeCm3: dest.volumeCm3 };
}
